<h2 mat-dialog-title class="dialog-header">
    <mat-icon class="mr-2 entity-policies">policy</mat-icon>
    {{ policy?.name || 'Policy Details' }}

    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="policy-details-content">
    <div class="policy-details-layout" *ngIf="policy">
        <!-- Left Sidebar -->
        <aside class="policy-sidebar">
            <!-- Edit Button -->
            <button
                mat-icon-button
                *ngIf="orgDialogService.mayManagePolicies"
                class="sidebar-edit-button"
                (click)="openEditDialog()"
                matTooltip="Edit Policy"
            >
                <mat-icon>edit</mat-icon>
            </button>

            <div class="policy-avatar-section">
                <div class="policy-avatar">
                    <mat-icon class="avatar-icon">policy</mat-icon>
                </div>
                <h3 class="policy-name">{{ policy.name | titlecase }}</h3>
                <span class="policy-version">Version {{ policy.version }}</span>
            </div>

            <!-- Status Badge -->
            <div class="status-section">
                <span
                    class="sidebar-status-badge"
                    [style.background]="getStatusConfig(policy.status).color"
                    style="color: white"
                >
                    <mat-icon>{{ getStatusConfig(policy.status).icon }}</mat-icon>
                    {{ getStatusConfig(policy.status).label }}
                </span>
            </div>

            <!-- Category -->
            <div class="sidebar-info-row">
                <mat-icon>{{ getCategoryIcon(policy.category) }}</mat-icon>
                <span>{{ getCategoryLabel(policy.category) }}</span>
            </div>

            <mat-divider></mat-divider>

            <!-- Key Dates -->
            <div class="sidebar-dates-section">
                <h4 class="sidebar-section-title">Key Dates</h4>

                <div class="date-row" *ngIf="policy.publishDate">
                    <mat-icon>event</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Publish Date</span>
                        <span class="date-value">{{ formatTimestamp(policy.publishDate) }}</span>
                    </div>
                </div>

                <div class="date-row" *ngIf="policy.effectiveDate">
                    <mat-icon>play_arrow</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Effective Date</span>
                        <span class="date-value">{{ formatTimestamp(policy.effectiveDate) }}</span>
                    </div>
                </div>

                <div class="date-row" *ngIf="policy.expiryDate">
                    <mat-icon>event_busy</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Expiry Date</span>
                        <span class="date-value">{{ formatTimestamp(policy.expiryDate) }}</span>
                    </div>
                </div>

                <div class="date-row">
                    <mat-icon>update</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Last Modified</span>
                        <span class="date-value">{{ formatTimestamp(policy.modifiedOn) }}</span>
                    </div>
                </div>

                <div class="date-row" *ngIf="policy.createdOn">
                    <mat-icon>add_circle</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Created</span>
                        <span class="date-value">{{ formatTimestamp(policy.createdOn) }}</span>
                    </div>
                </div>

                <div class="date-row" *ngIf="creatorName">
                    <mat-icon>person</mat-icon>
                    <div class="date-details">
                        <span class="date-label">Created By</span>
                        <span class="date-value">{{ creatorName }}</span>
                    </div>
                </div>
            </div>

            <mat-divider></mat-divider>

            <!-- Target Audience Summary -->
            <div class="sidebar-targets-section">
                <h4 class="sidebar-section-title">Target Audience</h4>

                <div class="target-mode-badge" *ngIf="getTargetsSummary().mode === 'all_organization'">
                    <mat-icon>corporate_fare</mat-icon>
                    <span>All Organization</span>
                </div>

                <div class="target-counts" *ngIf="getTargetsSummary().mode === 'selected'">
                    <div class="target-count-item" *ngIf="getTargetsSummary().partCount > 0">
                        <mat-icon>account_tree</mat-icon>
                        <span>{{ getTargetsSummary().partCount }} Parts</span>
                    </div>
                    <div class="target-count-item" *ngIf="getTargetsSummary().hatCount > 0">
                        <mat-icon>badge</mat-icon>
                        <span>{{ getTargetsSummary().hatCount }} Hats</span>
                    </div>
                    <div class="target-count-item" *ngIf="getTargetsSummary().userCount > 0">
                        <mat-icon>people</mat-icon>
                        <span>{{ getTargetsSummary().userCount }} Users</span>
                    </div>
                </div>

                <div class="no-targets-msg" *ngIf="!policy.targets || getTargetsSummary().mode === 'none'">
                    <mat-icon>info_outline</mat-icon>
                    <span>No targets configured</span>
                </div>
            </div>

            <mat-divider></mat-divider>

            <!-- Quick Stats -->
            <div class="policy-quick-stats">
                <div class="stat-row">
                    <mat-icon>history</mat-icon>
                    <span class="stat-label">{{ revisions.length }} REVISIONS</span>
                </div>
                <div class="stat-row">
                    <mat-icon>people</mat-icon>
                    <span class="stat-label">{{ usersViaHats.length }} USERS</span>
                </div>
                <div class="stat-row">
                    <mat-icon>business</mat-icon>
                    <span class="stat-label">{{ positionsWithPolicy.length }} POSITIONS</span>
                </div>
                <div class="stat-row">
                    <mat-icon>school</mat-icon>
                    <span class="stat-label">{{ hatsUsingPolicy.length }} HATS</span>
                </div>
            </div>
        </aside>

        <!-- Main Content -->
        <main class="policy-main-content">
            <mat-accordion class="sections-accordion" multi>
                <!-- Policy Content -->
                <mat-expansion-panel
                    [expanded]="sectionStates.content"
                    (expandedChange)="sectionStates.content = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-policies">description</mat-icon>
                            Policy Content
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div class="policy-writing" *ngIf="policy.policyWriting">
                        <div [innerHTML]="formattedPolicyContent"></div>
                    </div>

                    <div *ngIf="!policy.policyWriting" class="empty-section">
                        <span>No policy content available</span>
                    </div>
                </mat-expansion-panel>

                <!-- Users Assigned via Hats -->
                <mat-expansion-panel [expanded]="sectionStates.users" (expandedChange)="sectionStates.users = $event">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-users">people</mat-icon>
                            Users Assigned ({{ usersViaHats.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div *ngIf="usersViaHats.length > 0" class="users-list">
                        <div *ngFor="let user of usersViaHats" class="user-item">
                            <div
                                class="user-avatar"
                                [style.background-image]="'url(' + getUserAvatarUrl(user) + ')'"
                                style="background-size: cover; background-position: center"
                            ></div>
                            <div class="user-info">
                                <span class="user-name">{{ user.name }}</span>
                                <span class="user-hats">via {{ user.hatNames.join(', ') }}</span>
                            </div>
                            <!-- Acknowledgment Status Badge -->
                            <div class="user-ack-status" *ngIf="policy?.requiresAcknowledgment">
                                <span
                                    class="ack-badge acknowledged"
                                    *ngIf="user.acknowledgmentStatus === 'acknowledged'"
                                    [matTooltip]="'Acknowledged' + (user.acknowledgedAt ? ' on ' + formatDate(user.acknowledgedAt) : '')"
                                >
                                    <mat-icon>check_circle</mat-icon>
                                    Acknowledged
                                </span>
                                <span
                                    class="ack-badge pending"
                                    *ngIf="user.acknowledgmentStatus === 'pending'"
                                    matTooltip="Staff member has not yet acknowledged this policy"
                                >
                                    <mat-icon>schedule</mat-icon>
                                    Pending
                                </span>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="usersViaHats.length === 0" class="empty-section">
                        <span>No staff are assigned this policy</span>
                    </div>
                </mat-expansion-panel>

                <!-- Positions with This Policy -->
                <mat-expansion-panel
                    [expanded]="sectionStates.positions"
                    (expandedChange)="sectionStates.positions = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-positions">business</mat-icon>
                            Org Parts ({{ positionsWithPolicy.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div *ngIf="positionsWithPolicy.length > 0" class="positions-list">
                        <div
                            *ngFor="let position of positionsWithPolicy"
                            class="position-item"
                            [class.is-post]="position.partType === 'post'"
                        >
                            <div class="position-avatar">
                                <mat-icon>business</mat-icon>
                            </div>
                            <div class="position-info">
                                <span class="position-name">{{ position.name | titlecase }}</span>
                                <span class="position-hat">via {{ position.hatName }}</span>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="positionsWithPolicy.length === 0" class="empty-section">
                        <span>No org parts are linked to this policy</span>
                    </div>
                </mat-expansion-panel>

                <!-- Hats Using This Policy -->
                <mat-expansion-panel [expanded]="sectionStates.hats" (expandedChange)="sectionStates.hats = $event">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-hats">school</mat-icon>
                            Hats Using This Policy ({{ hatsUsingPolicy.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div *ngIf="hatsUsingPolicy.length > 0" class="hats-list">
                        <div *ngFor="let hat of hatsUsingPolicy" class="hat-item">
                            <div class="hat-avatar">
                                <mat-icon>school</mat-icon>
                            </div>
                            <div class="hat-info">
                                <span class="hat-name">{{ hat.name | titlecase }}</span>
                                <span class="ng-hat-location" *ngIf="(hat | hatLocation: data.organization) as hatWhere"
                                    >{{ hatWhere }}</span
                                >
                                <span class="hat-users">{{ hat.userCount }} user(s)</span>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="hatsUsingPolicy.length === 0" class="empty-section">
                        <span>No hats are using this policy</span>
                    </div>
                </mat-expansion-panel>

                <!-- Targeted Parts/Hats/Users (Intent) -->
                <mat-expansion-panel
                    [expanded]="sectionStates.targeting"
                    (expandedChange)="sectionStates.targeting = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon>track_changes</mat-icon>
                            Targeting Intent
                            <span class="section-count" *ngIf="getTargetsSummary().mode === 'all_organization'"
                                >(All Org)</span
                            >
                            <span class="section-count" *ngIf="getTargetsSummary().mode === 'selected'"
                                >({{ getTargetedParts().length + getTargetedHats().length + getTargetedUsers().length
                                }})</span
                            >
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <!-- All Organization Mode -->
                    <div *ngIf="getTargetsSummary().mode === 'all_organization'" class="target-intent-card all-org">
                        <mat-icon>corporate_fare</mat-icon>
                        <div class="target-intent-info">
                            <span class="target-intent-label">All Organization</span>
                            <span class="target-intent-description"
                                >This policy targets everyone in the organization</span
                            >
                        </div>
                    </div>

                    <!-- Selected Mode -->
                    <div *ngIf="getTargetsSummary().mode === 'selected'" class="target-intent-groups">
                        <!-- Targeted Parts -->
                        <div *ngIf="getTargetedParts().length > 0" class="target-intent-group">
                            <h5 class="target-group-title">
                                <mat-icon>account_tree</mat-icon>
                                Targeted Parts ({{ getTargetedParts().length }})
                            </h5>
                            <div class="target-items-list">
                                <div *ngFor="let part of getTargetedParts()" class="target-item">
                                    <mat-icon class="target-item-icon">folder</mat-icon>
                                    <div class="target-item-info">
                                        <span class="target-item-name">{{ part.name }}</span>
                                        <span *ngIf="part.partType" class="target-item-type">{{ part.partType }}</span>
                                        <span *ngIf="part.includeChildren" class="target-item-cascade">
                                            <mat-icon>subdirectory_arrow_right</mat-icon>
                                            Includes children
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Targeted Hats -->
                        <div *ngIf="getTargetedHats().length > 0" class="target-intent-group">
                            <h5 class="target-group-title">
                                <mat-icon>badge</mat-icon>
                                Targeted Hats ({{ getTargetedHats().length }})
                            </h5>
                            <div class="target-items-list">
                                <div *ngFor="let hat of getTargetedHats()" class="target-item">
                                    <mat-icon class="target-item-icon hat-icon">school</mat-icon>
                                    <span class="target-item-name">{{ hat.name }}</span>
                                    <span
                                        class="ng-hat-location"
                                        *ngIf="(hat | hatLocation: data.organization) as hatWhere"
                                        >{{ hatWhere }}</span
                                    >
                                </div>
                            </div>
                        </div>

                        <!-- Targeted Users -->
                        <div *ngIf="getTargetedUsers().length > 0" class="target-intent-group">
                            <h5 class="target-group-title">
                                <mat-icon>people</mat-icon>
                                Targeted Users ({{ getTargetedUsers().length }})
                            </h5>
                            <div class="target-items-list">
                                <div *ngFor="let user of getTargetedUsers()" class="target-item">
                                    <mat-icon class="target-item-icon user-icon">person</mat-icon>
                                    <div class="target-item-info">
                                        <span class="target-item-name">{{ user.name }}</span>
                                        <span *ngIf="user.email" class="target-item-email">{{ user.email }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- No Targets Configured -->
                    <div *ngIf="!hasConfiguredTargets()" class="empty-section">
                        <mat-icon>info_outline</mat-icon>
                        <span>No targeting configured yet</span>
                    </div>
                </mat-expansion-panel>

                <!-- Approval Workflow -->
                <mat-expansion-panel
                    *ngIf="hasApprovalWorkflow()"
                    [expanded]="sectionStates.approval"
                    (expandedChange)="sectionStates.approval = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon>approval</mat-icon>
                            Approval Workflow ({{ getApprovedStepsCount() }}/{{ getApprovalSteps().length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div class="approval-workflow-timeline">
                        <div
                            *ngFor="let step of getApprovalSteps(); let i = index; let last = last"
                            class="approval-step"
                            [class.current-step]="isCurrentStep(i)"
                        >
                            <div class="step-connector" *ngIf="!last"></div>
                            <div
                                class="step-icon"
                                [style.background]="getApprovalStepStatusConfig(step.status).color + '22'"
                                [style.borderColor]="getApprovalStepStatusConfig(step.status).color"
                            >
                                <mat-icon [style.color]="getApprovalStepStatusConfig(step.status).color">
                                    {{ getApprovalStepStatusConfig(step.status).icon }}
                                </mat-icon>
                            </div>
                            <div class="step-content">
                                <div class="step-header">
                                    <span class="step-name">{{ step.name || 'Step ' + (i + 1) }}</span>
                                    <span
                                        class="step-status-badge"
                                        [style.background]="getApprovalStepStatusConfig(step.status).color + '22'"
                                        [style.color]="getApprovalStepStatusConfig(step.status).color"
                                    >
                                        {{ getApprovalStepStatusConfig(step.status).label }}
                                    </span>
                                </div>
                                <div class="step-approver">
                                    <mat-icon>{{ step.approver.type === 'hat' ? 'school' : 'person' }}</mat-icon>
                                    <span>{{ step.approver.name || 'Unassigned' }}</span>
                                </div>
                                <div *ngIf="step.actionedAt" class="step-actioned">
                                    <mat-icon>event</mat-icon>
                                    <span>{{ formatTimestamp(step.actionedAt) }}</span>
                                </div>
                                <div *ngIf="step.comment" class="step-comment">
                                    <mat-icon>comment</mat-icon>
                                    <span>{{ step.comment }}</span>
                                </div>
                                <!-- Only the approver this step is waiting on sees these (OE-75) -->
                                <div *ngIf="canDecideStep(step, i)" class="step-actions">
                                    <button
                                        mat-flat-button
                                        color="primary"
                                        [disabled]="approvalInFlight"
                                        (click)="approveStep(step, i)"
                                    >
                                        <mat-icon>check_circle</mat-icon>
                                        Approve
                                    </button>
                                    <button
                                        mat-stroked-button
                                        color="warn"
                                        [disabled]="approvalInFlight"
                                        (click)="rejectStep(step, i)"
                                    >
                                        <mat-icon>cancel</mat-icon>
                                        Reject
                                    </button>
                                </div>
                                <div
                                    *ngIf="isCurrentStep(i) && !canDecideStep(step, i) && step.status === 'pending'"
                                    class="step-waiting-note"
                                >
                                    <mat-icon>hourglass_empty</mat-icon>
                                    <span>Waiting on {{ step.approver.name || 'this approver' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Frozen-approver rule (design §2c) -->
                    <div *ngIf="getWorkflowStatus() === 'in_progress'" class="approval-frozen-note">
                        <mat-icon>info_outline</mat-icon>
                        <span>
                            Approvers were fixed when this policy was submitted — re-staffing a position now does not
                            change who is asked. Reject and resubmit, or override, if the chain must change.
                        </span>
                    </div>

                    <!-- Owner/admin bypass of the outstanding steps -->
                    <div *ngIf="canOverrideApproval()" class="approval-override-actions">
                        <button
                            mat-stroked-button
                            color="warn"
                            [disabled]="approvalInFlight"
                            (click)="overrideApproval()"
                        >
                            <mat-icon>gavel</mat-icon>
                            Override &amp; Approve
                        </button>
                        <span class="override-hint"
                            >Skips the remaining steps. The reason is recorded on the policy.</span
                        >
                    </div>
                </mat-expansion-panel>

                <!-- No Approval Workflow -->
                <mat-expansion-panel
                    *ngIf="!hasApprovalWorkflow()"
                    [expanded]="sectionStates.approval"
                    (expandedChange)="sectionStates.approval = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon>approval</mat-icon>
                            Approval Workflow
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="empty-section">
                        <mat-icon>info_outline</mat-icon>
                        <span>No approval workflow configured</span>
                    </div>
                </mat-expansion-panel>

                <!-- Admin Override Notice -->
                <mat-expansion-panel
                    *ngIf="policy.adminOverride?.wasOverridden"
                    [expanded]="sectionStates.adminOverride"
                    (expandedChange)="sectionStates.adminOverride = $event"
                    class="admin-override-panel"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title class="warning-title">
                            <mat-icon>admin_panel_settings</mat-icon>
                            Admin Override
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="admin-override-notice">
                        <div class="override-header">
                            <mat-icon class="warning-icon">warning</mat-icon>
                            <span>This policy was approved via admin override</span>
                        </div>
                        <div class="override-details">
                            <div class="override-detail-row">
                                <span class="detail-label">Overridden By:</span>
                                <span class="detail-value"
                                    >{{ policy.adminOverride?.overriddenByEmail || policy.adminOverride?.overriddenBy
                                    }}</span
                                >
                            </div>
                            <div class="override-detail-row">
                                <span class="detail-label">Override Date:</span>
                                <span class="detail-value"
                                    >{{ formatTimestamp(policy.adminOverride?.overriddenAt) }}</span
                                >
                            </div>
                            <div class="override-detail-row reason-row">
                                <span class="detail-label">Reason:</span>
                                <span class="detail-value reason-value"
                                    >{{ policy.adminOverride?.overrideReason }}</span
                                >
                            </div>
                        </div>
                    </div>
                </mat-expansion-panel>

                <!-- Audit Trail (collapsed by default) -->
                <mat-expansion-panel
                    *ngIf="policy.auditTrail && policy.auditTrail.length > 0"
                    [expanded]="sectionStates.auditTrail"
                    (expandedChange)="sectionStates.auditTrail = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon>history_edu</mat-icon>
                            Audit Trail ({{ policy.auditTrail.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="audit-trail-list">
                        <div *ngFor="let entry of policy.auditTrail" class="audit-entry">
                            <div class="audit-entry-icon" [ngClass]="getAuditEntryClass(entry.action)">
                                <mat-icon>{{ getAuditEntryIcon(entry.action) }}</mat-icon>
                            </div>
                            <div class="audit-entry-content">
                                <div class="audit-entry-header">
                                    <span class="audit-action">{{ formatAuditAction(entry.action) }}</span>
                                    <span class="audit-time">{{ formatTimestamp(entry.timestamp) }}</span>
                                </div>
                                <div class="audit-entry-user" *ngIf="entry.performedByEmail || entry.performedBy">
                                    <mat-icon>person</mat-icon>
                                    <span>{{ entry.performedByEmail || entry.performedBy }}</span>
                                </div>
                                <div class="audit-entry-reason" *ngIf="entry.overrideReason">
                                    <mat-icon>comment</mat-icon>
                                    <span>{{ entry.overrideReason }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </mat-expansion-panel>

                <!-- Revision History -->
                <mat-expansion-panel
                    [expanded]="sectionStates.revisions"
                    (expandedChange)="sectionStates.revisions = $event"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon>history</mat-icon>
                            Revision History ({{ revisions.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <!-- Restore availability info -->
                    <div class="restore-info" *ngIf="revisions.length > 0 && !canRestoreRevision()">
                        <mat-icon>info</mat-icon>
                        <span>{{ getRestoreNotAvailableReason() }}</span>
                    </div>

                    <mat-accordion *ngIf="revisions.length > 0" class="revisions-accordion">
                        <mat-expansion-panel *ngFor="let revision of revisions">
                            <mat-expansion-panel-header>
                                <mat-panel-title>
                                    <mat-icon class="revision-icon">bookmark</mat-icon>
                                    <span class="revision-number">Rev {{ revision.revisionNumber }}</span>
                                    <span class="revision-changes" *ngIf="revision.changes?.length">
                                        <span
                                            class="change-chip"
                                            *ngFor="let change of revision.changes"
                                            [class.initial-chip]="change.field === 'initial'"
                                            [matTooltip]="change.fromValue && change.toValue ? change.label + ': ' + change.fromValue + ' → ' + change.toValue : change.label"
                                        >
                                            <mat-icon class="change-icon">{{ change.icon }}</mat-icon>
                                            {{ change.label }}
                                        </span>
                                    </span>
                                </mat-panel-title>
                                <mat-panel-description> {{ formatDate(revision.date) }} </mat-panel-description>
                            </mat-expansion-panel-header>

                            <div class="revision-details">
                                <!-- What Changed Section (for non-initial revisions) -->
                                <div
                                    class="revision-changes-detail"
                                    *ngIf="!revision.isInitial && revision.changes?.length"
                                >
                                    <span class="section-label">What Changed:</span>
                                    <div class="changes-list">
                                        <div class="change-detail" *ngFor="let change of revision.changes">
                                            <div class="change-header">
                                                <mat-icon class="change-detail-icon">{{ change.icon }}</mat-icon>
                                                <span class="change-field">{{ change.label }}</span>
                                            </div>
                                            <div class="change-values" *ngIf="change.fromValue || change.toValue">
                                                <div class="from-value" *ngIf="change.fromValue">
                                                    <span class="value-label">From:</span>
                                                    <span class="value-text">{{ change.fromValue }}</span>
                                                </div>
                                                <mat-icon class="arrow-icon">arrow_forward</mat-icon>
                                                <div class="to-value" *ngIf="change.toValue">
                                                    <span class="value-label">To:</span>
                                                    <span class="value-text">{{ change.toValue }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Revision Snapshot Details -->
                                <div class="revision-snapshot">
                                    <span class="section-label">Snapshot at this revision:</span>
                                    <div class="revision-meta-grid">
                                        <div class="revision-meta">
                                            <span class="meta-label">Name:</span>
                                            <span class="meta-value">{{ revision.name }}</span>
                                        </div>
                                        <div class="revision-meta" *ngIf="revision.writtenBy">
                                            <span class="meta-label">Written By:</span>
                                            <span class="meta-value">{{ revision.writtenBy }}</span>
                                        </div>
                                        <div class="revision-meta">
                                            <span class="meta-label">Date:</span>
                                            <span class="meta-value">{{ formatDate(revision.date) }}</span>
                                        </div>
                                        <div class="revision-meta" *ngIf="revision.category">
                                            <span class="meta-label">Category:</span>
                                            <span class="meta-value">{{ revision.category }}</span>
                                        </div>
                                        <div class="revision-meta" *ngIf="revision.status">
                                            <span class="meta-label">Status:</span>
                                            <span class="meta-value">{{ revision.status }}</span>
                                        </div>
                                        <div class="revision-meta" *ngIf="revision.effectiveDate">
                                            <span class="meta-label">Effective Date:</span>
                                            <span class="meta-value">{{ formatDate(revision.effectiveDate) }}</span>
                                        </div>
                                        <div class="revision-meta" *ngIf="revision.expiryDate">
                                            <span class="meta-label">Expiry Date:</span>
                                            <span class="meta-value">{{ formatDate(revision.expiryDate) }}</span>
                                        </div>
                                        <div
                                            class="revision-meta"
                                            *ngIf="revision.targets?.partCount || revision.targets?.directHatCount || revision.targets?.directUserCount"
                                        >
                                            <span class="meta-label">Targets:</span>
                                            <span class="meta-value">
                                                <span *ngIf="revision.targets?.partCount"
                                                    >{{ revision.targets?.partCount }} parts</span
                                                >
                                                <span *ngIf="revision.targets?.directHatCount"
                                                    >, {{ revision.targets?.directHatCount }} hats</span
                                                >
                                                <span *ngIf="revision.targets?.directUserCount"
                                                    >, {{ revision.targets?.directUserCount }} users</span
                                                >
                                            </span>
                                        </div>
                                        <div
                                            class="revision-meta"
                                            *ngIf="revision.approvalWorkflow?.approverNames?.length"
                                        >
                                            <span class="meta-label">Approvers:</span>
                                            <span class="meta-value"
                                                >{{ revision.approvalWorkflow?.approverNames?.join(' → ') }}</span
                                            >
                                        </div>
                                    </div>
                                </div>

                                <!-- Change Notes -->
                                <div class="revision-change-notes" *ngIf="revision.changeNotes">
                                    <span class="meta-label">Change Notes:</span>
                                    <p class="change-notes-text">{{ revision.changeNotes }}</p>
                                </div>

                                <!-- Content Preview with proper HTML rendering -->
                                <div class="revision-content-preview" *ngIf="revision.policyWriting">
                                    <span class="meta-label">Policy Content:</span>
                                    <div class="content-preview-html" [innerHTML]="revision.policyWriting"></div>
                                </div>

                                <!-- Revision Actions -->
                                <div class="revision-actions">
                                    <button
                                        mat-stroked-button
                                        color="primary"
                                        (click)="previewRevision(revision)"
                                        *ngIf="revision.policyWriting"
                                    >
                                        <mat-icon>visibility</mat-icon>
                                        View Full Content
                                    </button>
                                    <button
                                        mat-stroked-button
                                        color="accent"
                                        (click)="restoreToRevision(revision)"
                                        [disabled]="!canRestoreRevision() || isLatestRevision(revision)"
                                        [matTooltip]="isLatestRevision(revision) ? 'This is the current revision' : (canRestoreRevision() ? 'Restore policy to this version' : getRestoreNotAvailableReason())"
                                    >
                                        <mat-icon>restore</mat-icon>
                                        Restore to This Version
                                    </button>
                                </div>
                            </div>
                        </mat-expansion-panel>
                    </mat-accordion>

                    <div *ngIf="revisions.length === 0" class="empty-section">
                        <span>No revision history available</span>
                    </div>
                </mat-expansion-panel>
            </mat-accordion>

            <!-- Revision Content Preview Modal -->
            <div class="revision-preview-overlay" *ngIf="selectedRevision" (click)="closeRevisionPreview()">
                <div class="revision-preview-modal" (click)="$event.stopPropagation()">
                    <div class="revision-preview-header">
                        <h3>
                            <mat-icon>bookmark</mat-icon>
                            Version {{ selectedRevision.version }} - {{ selectedRevision.name }}
                        </h3>
                        <button mat-icon-button (click)="closeRevisionPreview()">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>

                    <!-- Full Snapshot Details -->
                    <div class="revision-snapshot-details">
                        <!-- Basic Info Row -->
                        <div class="snapshot-row">
                            <div class="snapshot-item">
                                <mat-icon>person</mat-icon>
                                <span class="snapshot-label">Written By:</span>
                                <span class="snapshot-value">{{ selectedRevision.writtenBy }}</span>
                            </div>
                            <div class="snapshot-item">
                                <mat-icon>event</mat-icon>
                                <span class="snapshot-label">Date:</span>
                                <span class="snapshot-value">{{ formatDate(selectedRevision.date) }}</span>
                            </div>
                            <div class="snapshot-item" *ngIf="selectedRevision.category">
                                <mat-icon>category</mat-icon>
                                <span class="snapshot-label">Category:</span>
                                <span class="snapshot-value">{{ selectedRevision.category }}</span>
                            </div>
                            <div class="snapshot-item" *ngIf="selectedRevision.status">
                                <mat-icon>flag</mat-icon>
                                <span class="snapshot-label">Status:</span>
                                <span class="snapshot-value">{{ selectedRevision.status }}</span>
                            </div>
                        </div>

                        <!-- Description -->
                        <div class="snapshot-section" *ngIf="selectedRevision.description">
                            <span class="snapshot-section-label"><mat-icon>notes</mat-icon> Description:</span>
                            <div class="snapshot-section-content">{{ selectedRevision.description }}</div>
                        </div>

                        <!-- Dates Row -->
                        <div
                            class="snapshot-row"
                            *ngIf="selectedRevision.effectiveDate || selectedRevision.publishDate || selectedRevision.expiryDate"
                        >
                            <div class="snapshot-item" *ngIf="selectedRevision.effectiveDate">
                                <mat-icon>event_available</mat-icon>
                                <span class="snapshot-label">Effective:</span>
                                <span class="snapshot-value">{{ formatDate(selectedRevision.effectiveDate) }}</span>
                            </div>
                            <div class="snapshot-item" *ngIf="selectedRevision.publishDate">
                                <mat-icon>publish</mat-icon>
                                <span class="snapshot-label">Publish:</span>
                                <span class="snapshot-value">{{ formatDate(selectedRevision.publishDate) }}</span>
                            </div>
                            <div class="snapshot-item" *ngIf="selectedRevision.expiryDate">
                                <mat-icon>event_busy</mat-icon>
                                <span class="snapshot-label">Expiry:</span>
                                <span class="snapshot-value">{{ formatDate(selectedRevision.expiryDate) }}</span>
                            </div>
                        </div>

                        <!-- Settings Row -->
                        <div class="snapshot-row">
                            <div class="snapshot-item" *ngIf="selectedRevision.targets">
                                <mat-icon>groups</mat-icon>
                                <span class="snapshot-label">Target Audience:</span>
                                <span class="snapshot-value"
                                    >{{ formatTargets(selectedRevision.targets) || 'All Organization' }}</span
                                >
                            </div>
                            <div class="snapshot-item">
                                <mat-icon>fact_check</mat-icon>
                                <span class="snapshot-label">Requires Ack:</span>
                                <span class="snapshot-value"
                                    >{{ selectedRevision.requiresAcknowledgment ? 'Yes' : 'No' }}</span
                                >
                            </div>
                            <div class="snapshot-item" *ngIf="selectedRevision.approvalWorkflow">
                                <mat-icon>how_to_reg</mat-icon>
                                <span class="snapshot-label">Approvers:</span>
                                <span class="snapshot-value"
                                    >{{ formatApprovers(selectedRevision.approvalWorkflow) || '(none)' }}</span
                                >
                            </div>
                        </div>

                        <!-- Change Notes -->
                        <div class="snapshot-section change-notes" *ngIf="selectedRevision.changeNotes">
                            <span class="snapshot-section-label"><mat-icon>edit_note</mat-icon> Change Notes:</span>
                            <div class="snapshot-section-content">{{ selectedRevision.changeNotes }}</div>
                        </div>
                    </div>

                    <!-- Policy Content -->
                    <div class="snapshot-section policy-content-section">
                        <span class="snapshot-section-label"><mat-icon>article</mat-icon> Policy Content:</span>
                        <div class="revision-preview-content" [innerHTML]="selectedRevision.policyWriting"></div>
                    </div>

                    <div class="revision-preview-actions">
                        <button mat-stroked-button (click)="closeRevisionPreview()">Close</button>
                        <button
                            mat-raised-button
                            color="accent"
                            (click)="restoreToRevision(selectedRevision)"
                            [disabled]="!canRestoreRevision()"
                            *ngIf="canRestoreRevision()"
                        >
                            <mat-icon>restore</mat-icon>
                            Restore to This Version
                        </button>
                    </div>
                </div>
            </div>
        </main>
    </div>

    <!-- No Data State -->
    <div *ngIf="!policy" class="no-data-state">
        <mat-icon>error_outline</mat-icon>
        <p>Policy data not available</p>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-raised-button color="primary" *ngIf="canApproveDirectly()" (click)="approvePolicy()">
        <mat-icon>check_circle</mat-icon>
        Approve Policy
    </button>
    <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>
